import { Alert, Checkbox, FormControlLabel } from "@mui/material";
import * as React from "react";
import { ISessionState } from "../../../models/session/sessionState";
import { Base } from "ui/Scripts/source/framework/base";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { IRecordEditItemFamiliarization, RecordEditItemFamiliarization } from "../../../models/record/recordEditItemFamiliarization";
import { IRecordFamiliarizationListItem } from "ui/Scripts/source/models/recordFamiliarization/recordFamiliarizationListItem";
import * as recordService from "../../../models/services/recordService";
import { ActionContainer } from "../../framework/ActionContainer";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { RecordEditListFirstCol } from "./RecordEditListFirstCol";
import { RecordUserFamiliarizationDialog } from "../RecordUserFamiliarizationDialog/RecordUserFamiliarizationDialog";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface IRecordEditFamiliarizationListProp {
    sessionState: ISessionState;
    isReadOnly: boolean;
    companyId: string;
    recordId: string;
    unitId: string;
    userId: string;
    userCode: string;
    familiarizationTrainers: string[];
    recordFamiliarizations: IRecordEditItemFamiliarization[];
    familiarizations: IRecordFamiliarizationListItem[];
    attachments: IFileAttachment[];
    onRemoved: (ids: string[]) => void;
    onSaved: (recordFamiliarization: IRecordEditItemFamiliarization) => void;
    onAttachmentsRemoved: (ids: string[]) => void;
    onAttachmentsAdded: (attachments: IFileAttachment[]) => void;
}

interface IRecordEditFamiliarizationListState {
    // List
    isLoading: boolean;
    items: IRecordEditItemFamiliarization[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];
    showArchived: boolean;
    selectedItemHasPrevious: boolean;
    selectedItemHasNext: boolean;
    familiarizationTrainers: string[];

    // Dialog
    item: IRecordEditItemFamiliarization;
    showEditDialog: boolean;
}

export class RecordEditFamiliarizationList extends React.Component<IRecordEditFamiliarizationListProp, IRecordEditFamiliarizationListState> {
    getFilteredList = (filter: string, showArchived: boolean, column: string, asc: boolean): IRecordEditItemFamiliarization[] => {
        const props = this.props;
        let result = props.recordFamiliarizations ? props.recordFamiliarizations.slice(0) : [];
        RecordEditItemFamiliarization.setFamiliarizationCodes(result, props.familiarizations);
        result = result.filter(i => showArchived ? true : i.activeState > 0.5);
        if (filter) {
            const lowFilter = filter.toLowerCase();
            result = result.filter(i => i.familiarizationCode.toLowerCase().indexOf(lowFilter) > -1 || i.comment.toLowerCase().indexOf(lowFilter) > -1);
        }
        RecordEditItemFamiliarization.sortRecordEditItemFamiliarizations(result, column, asc);
        return result;
    };

    constructor(props: IRecordEditFamiliarizationListProp) {
        super(props);
        const recordFamiliarizations = this.getFilteredList("", false, "startDate", false);
        this.state = {
            isLoading: false,
            items: recordFamiliarizations,
            filter: "",
            sortColumn: "startDate",
            sortOrderIsAsc: false,
            selectedId: null,
            checkedIds: [],
            showArchived: false,
            selectedItemHasPrevious: false,
            selectedItemHasNext: false,
            item: null,
            showEditDialog: false,
            familiarizationTrainers: Base.getUniqueStringItems(props.familiarizationTrainers.concat(props.recordFamiliarizations.map(i => i.trainer).filter(i => !!i)))
        };
    }

    filteredList = (filter: string, showArchived: boolean, column: string, asc: boolean) => {
        const items = this.getFilteredList(filter, showArchived, column, asc);
        const item = this.state.showEditDialog && this.state.item
            ? items.find(i => i.id === this.state.item.id)
            : null;
        const index = item ? items.findIndex(i => i.id === item.id) : -1;
        this.setState({
            filter,
            showArchived,
            sortColumn: column,
            sortOrderIsAsc: asc,
            items,
            item,
            showEditDialog: !!item,
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < items.length - 1
        });
    };

    componentDidUpdate(prevProps: IRecordEditFamiliarizationListProp, prevState: IRecordEditFamiliarizationListState) {
        const props = this.props;
        const state = this.state;
        if (EditItem.getHash(prevProps.recordFamiliarizations) === EditItem.getHash(props.recordFamiliarizations)) return;
        this.filteredList(state.filter, state.showArchived, state.sortColumn, state.sortOrderIsAsc);
        this.setState({
            familiarizationTrainers: Base.getUniqueStringItems(props.familiarizationTrainers.concat(props.recordFamiliarizations.map(i => i.trainer).filter(i => !!i)))
        });
    };

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.showArchived, state.sortColumn, state.sortOrderIsAsc);
    };

    changeShowArchived = (showArchived: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, showArchived, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, state.showArchived, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getRemoveSaveData = (props: IRecordEditFamiliarizationListProp, recordFamiliarizationIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("ids", recordFamiliarizationIds);
        return result;
    };

    handleRemove = async () => {
        const obj = this;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const familiarization = state.items.find(i => i.id === selectedIds[0]);
        if (!familiarization) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveRecordFamiliarizationParameter, familiarization.familiarizationCode), false) === ConfirmationDialogResult.Yes) {
            const saveData = this.getRemoveSaveData(this.props, selectedIds);
            // Call server
            const result = await AppUtils.callService(() => recordService.recordEditRemoveFamiliarizations(saveData.obj));
            if (!result) return;
            obj.props.onRemoved(selectedIds);
        }
    };

    static createNewRecordEditItemFamiliarization = (familiarizations: IRecordFamiliarizationListItem[]): RecordEditItemFamiliarization => {
        const result = new RecordEditItemFamiliarization();
        result.id = Base.getGuid();
        result.familiarizationId = familiarizations.length > 0 ? familiarizations[0].id : "";
        result.startDate = Base.getNowUtcDate().addDays(-1).getTime();
        result.activeState = 1;
        result.setFamiliarizationCode(familiarizations);
        return result;
    };
    
    handleAdd = () => {
        const props = this.props;
        if (props.familiarizations.length < 1) return;
        this.setState({
            item: RecordEditFamiliarizationList.createNewRecordEditItemFamiliarization(props.familiarizations),
            showEditDialog: true
        });
    };

    handleOpen = (id: string) => {
        if (!id) return;
        const state = this.state;
        const index = state.items.findIndex(i => i.id === id);
        if (index < 0) return;
        this.setState({
            selectedId: id,
            item: state.items[index],
            showEditDialog: true,
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < state.items.length - 1
        });
    };

    onRecordUserFamiliarizationDialogSaved = async (recordFamiliarization: IRecordEditItemFamiliarization, closeDialog: boolean) => {
        if (!recordFamiliarization) return;
        this.setState({
            showEditDialog: !closeDialog,
            selectedId: recordFamiliarization.id,
            item: recordFamiliarization
        });
        this.props.onSaved(recordFamiliarization);
    };

    onRecordUserFamiliarizationDialogClose = (handlers: IModifyUser[]) => {
        const state = this.state;
        this.setState({
            showEditDialog: false
        });
        if (!state.item) return;
        state.item.handlers = handlers;
        this.props.onSaved(state.item);
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        if (index < 0 || index >= state.items.length) return;
        this.setState({
            selectedId: state.items[index].id,
            item: state.items[index],
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < state.items.length - 1
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const familiarizationsSpecified = props.familiarizations.length > 0;
        return (<>
            <VerticalContainer
                before={<>
                    <ActionContainer
                        leftItems={
                            <ListSearchFilter
                                searchFilter={state.filter}
                                onSearchClick={this.changeFilter}
                                appendElements={[
                                    <FormControlLabel
                                        key="showArchived"
                                        label={Translations.ShowArchived}
                                        control={
                                            <Checkbox
                                                checked={state.showArchived}
                                                onChange={(_e, checked) => this.changeShowArchived(checked)}
                                            />
                                        }
                                    />
                                ]}
                            />
                        }
                        rightItems={
                            <ListIconButtonsItem
                                addTooltip={Translations.AddFamiliarization}
                                removeTooltip={Translations.RemoveFamiliarization}
                                addDisabled={props.isReadOnly || !familiarizationsSpecified}
                                removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                onAdd={this.handleAdd}
                                onRemove={this.handleRemove}
                            />
                        }
                    />
                    {!!props.isReadOnly &&
                        <Alert severity="info" sx={{ marginBottom: "1rem" }}>{Translations.NewRecordUserMustBeSavedBeforeFamiliarizationsCanBeAdded}</Alert>
                    }
                    {!props.isReadOnly && !familiarizationsSpecified &&
                        <Alert severity="info" sx={{ marginBottom: "1rem" }}>{Translations.FamiliarizationsMustBeDefinedBeforeAddingRecordUserFamiliarizations}</Alert>
                    }
                </>}
                main={<>
                    {!props.isReadOnly &&
                        <List
                            intialized={true}
                            isLoading={state.isLoading}
                            hasMore={false}
                            height="100%"
                            globalLoadingInUse={false}
                            header={{
                                columns: [
                                    { size: 3, column: "code", content: Translations.Subject }, // "col-xl-6 col-3"
                                    { size: 2, column: "startDate", content: Translations.FamiliarizationStartDate }, // "col-xl-1 col-2"
                                    { size: 2, column: "endDate", content: Translations.FamiliarizationEndDate }, // "col-xl-1 col-2"
                                    { size: 2, column: "validityEndDate", content: Translations.ValidityEndDate }, // "col-xl-1 col-2"
                                    { size: 2, column: "trainer", content: Translations.FamiliarizationTrainer }, // "col-xl-2 col-2"
                                    { size: 1, column: "activeState", content: Translations.Archived } // "col-xl-1 col-1"
                                ]
                            }}
                            lines={
                                state.items.map((recordFamiliarization) => {
                                    return {
                                        columns: [
                                            {
                                                size: 3,
                                                content:
                                                    <RecordEditListFirstCol
                                                        id={recordFamiliarization.id}
                                                        code={recordFamiliarization.familiarizationCode}
                                                        userState={recordFamiliarization.getUserState(props.sessionState.code)}
                                                        attachments={props.attachments}
                                                        onClick={this.handleOpen}
                                                    />
                                            },
                                            { size: 2, content: Base.utcTimeToDateStr(recordFamiliarization.startDate) },
                                            { size: 2, content: Base.utcTimeToDateStr(recordFamiliarization.endDate) },
                                            { size: 2, content: Base.utcTimeToDateStr(recordFamiliarization.validityEndDate) },
                                            { size: 2, content: recordFamiliarization.trainer },
                                            { size: 1, content: recordFamiliarization.activeState > 0.5 ? Translations.No : Translations.Yes }
                                        ],
                                        id: recordFamiliarization.id
                                    };
                                })
                            }
                            noDataMessage={Translations.AddFamiliarizations}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                            onOpenLine={this.handleOpen}
                        />
                    }
                </>}
            />
            {state.showEditDialog &&
                <RecordUserFamiliarizationDialog
                    sessionState={props.sessionState}
                    companyId={props.companyId}
                    recordId={props.recordId}
                    unitId={props.unitId}
                    userId={props.userId}
                    userCode={props.userCode}
                    familiarizations={props.familiarizations}
                    familiarizationTrainers={state.familiarizationTrainers}
                    item={state.item}
                    attachments={props.attachments}
                    hasPrevious={state.selectedItemHasPrevious}
                    hasNext={state.selectedItemHasNext}
                    onSaved={this.onRecordUserFamiliarizationDialogSaved}
                    onClose={this.onRecordUserFamiliarizationDialogClose}
                    onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                    onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                    onAttachmentsRemoved={props.onAttachmentsRemoved}
                    onAttachmentsAdded={props.onAttachmentsAdded}
                />
            }
        </>);
    }
}